import type {
    ContactPhone,
    FaqItem,
    Feature,
    NavLink,
    Program,
    SelectOption,
    SocialLink,
    Stat,
    TrainingCenter
} from '@/types'

import {
    Award,
    Bell,
    BookOpen,
    Braces,
    BrainCircuit,
    CalendarDays,
    ClipboardCheck,
    Facebook,
    Headset,
    Instagram,
    Layers,
    LogIn,
    Megaphone,
    MessageCircle,
    MonitorSmartphone,
    Phone,
    Receipt,
    ShieldCheck,
    Sparkles,
    Star,
    Users
} from 'lucide-react'

const mapsSearch = (query: string): string => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`

export const navLinks: Array<NavLink> = [
    { label: "Programs", href: '/#programs' },
    { label: "About", href: '/#about' },
    { label: "Portal", href: '/#portal' },
    { label: "Centers", href: '/#centers' },
    { label: "FAQ", href: '/#faq' }
]

export const stats: Array<Stat> = [
    { value: "10", suffix: "+", label: "Years building software", detail: "Full-stack web and software delivery" },
    { value: "100", suffix: "+", label: "Projects delivered", detail: "Web, software and automation work" },
    { value: "3", label: "Countries served", detail: "Clients in the USA, UK and India" },
    { value: "2", label: "Training centers", detail: "Hyderabad and Vijayawada" }
]

export const programs: Array<Program> = [
    {
        id: 'full-stack-web',
        title: "Full-Stack Web Development",
        summary: "Design, build and deploy modern websites and web applications from scratch, from the screens people use to the database behind them.",
        skills: ["Front end", "Back end", "Databases", "Deployment"],
        icon: MonitorSmartphone
    },
    {
        id: 'software-development',
        title: "Software Development",
        summary: "Design, code and test software applications in industry programming languages, the way working development teams do.",
        skills: ["Programming", "Application design", "Testing"],
        icon: Braces
    },
    {
        id: 'ai-projects',
        title: "AI Projects",
        summary: "Gain practical experience by building intelligent, real-world artificial intelligence projects with guidance at every step.",
        skills: ["Machine learning", "Working with data", "Real use cases"],
        icon: BrainCircuit
    },
    {
        id: 'digital-marketing',
        title: "Digital Marketing",
        summary: "Learn SEO, social media and online advertising, and grow into a certified digital marketing professional.",
        skills: ["SEO", "Social media", "Online ads"],
        icon: Megaphone
    },
    {
        id: 'qa-it-support',
        title: "Software Testing & IT Support",
        summary: "Prepare for semi-IT roles in software quality assurance testing and the IT support work that keeps teams running.",
        skills: ["QA testing", "IT support", "Troubleshooting"],
        icon: ShieldCheck
    },
    {
        id: 'customer-support',
        title: "Customer Support (BPO)",
        summary: "Train for customer service roles as a support representative or chat executive, with a focus on clear and confident communication.",
        skills: ["Voice support", "Chat support", "Communication"],
        icon: Headset
    }
]

export const pillars: Array<Feature> = [
    {
        title: "Hands-on full-stack projects",
        text: "Work on real builds from planning to deployment instead of practice exercises that never ship.",
        icon: Layers
    },
    {
        title: "End-to-end development support",
        text: "Trainers stay with you at every stage, from your first line of code to going live.",
        icon: Users
    },
    {
        title: "Technology for every business need",
        text: "Learn the tools businesses actually use across web, software, AI and digital marketing.",
        icon: Sparkles
    }
]

export const studentPortalFeatures: Array<Feature> = [
    { title: "Batch and schedule", text: "Your course, batch timings and upcoming sessions.", icon: CalendarDays },
    { title: "Attendance", text: "A record of every session you have attended.", icon: ClipboardCheck },
    { title: "Fees and receipts", text: "Fee dues, reminders and receipts in one place.", icon: Receipt },
    { title: "Certificates", text: "The certificates issued for your program.", icon: Award }
]

export const trainerPortalFeatures: Array<Feature> = [
    { title: "Your batches", text: "The batches, courses and schedules assigned to you.", icon: Users },
    { title: "Session attendance", text: "Mark attendance for each session you run.", icon: ClipboardCheck },
    { title: "Results", text: "Record results and follow how each student is doing.", icon: BookOpen },
    { title: "Notices", text: "Share updates and announcements with your batches.", icon: Bell }
]

export const signInSteps: Array<string> = [
    "Choose Student or Trainer on the sign-in card.",
    "Pick the training center you registered with.",
    "Enter your registered mobile number and password."
]

export const centers: Array<TrainingCenter> = [
    {
        id: 'hyderabad',
        city: "Hyderabad",
        area: "Gachibowli",
        addressLines: ["1-23/1/2, Greenland Colony", "Gachibowli, Hyderabad", "Telangana 500032"],
        mapUrl: mapsSearch("1-23/1/2 Greenland Colony, Gachibowli, Hyderabad, Telangana 500032")
    },
    {
        id: 'vijayawada',
        city: "Vijayawada",
        area: "Bharathi Nagar",
        addressLines: ["3rd Floor, Saiprabha Complex", "Bharathi Nagar, Vijayawada", "Andhra Pradesh 520008"],
        mapUrl: mapsSearch("Saiprabha Complex, Bharathi Nagar, Vijayawada, Andhra Pradesh 520008")
    }
]

export const contactPhones: Array<ContactPhone> = [
    { display: "040 4614 4553", href: 'tel:+914046144553' },
    { display: "+91 83973 70000", href: 'tel:+918397370000' },
    { display: "+91 82973 78887", href: 'tel:+918297378887' }
]

export const contactEmail = 'hr@webifytechnologies.com'

export const socialLinks: Array<SocialLink> = [
    { label: "Instagram", href: 'https://www.instagram.com/webifytech25/', icon: Instagram },
    { label: "Facebook", href: 'https://www.facebook.com/share/1BmEKHzxmV/', icon: Facebook },
    { label: "WhatsApp channel", href: 'https://whatsapp.com/channel/0029VbByUfJ84OmBoZjwWn1Z', icon: MessageCircle },
    { label: "Google reviews", href: 'https://g.page/r/CUUhNB1LpfFNEAE/review', icon: Star }
]

export const faqs: Array<FaqItem> = [
    {
        question: "Who can register for a Webify program?",
        answer: "B.Tech, Degree and Diploma students and graduates. Pick your qualification in the registration form so the team can place you in the right batch."
    },
    {
        question: "How do I get my portal login?",
        answer: "After you register, your training center confirms your batch and activates portal access on the mobile number you registered with. You then sign in as a Student using that number."
    },
    {
        question: "How much do the programs cost?",
        answer: "Fees depend on the track and the batch you join. The team shares the exact fee for your track and center when they call you after you register."
    },
    {
        question: "Will I work on real projects?",
        answer: "Yes. Webify is a working software company, and its internships are built around hands-on project work rather than theory alone."
    },
    {
        question: "I forgot my portal password. What should I do?",
        answer: "Choose Forgot password on the sign-in card, or call your training center on 040 4614 4553 and the team will help you get back in."
    },
    {
        question: "Where are the training centers?",
        answer: "Webify trains students at Gachibowli in Hyderabad and at Bharathi Nagar in Vijayawada. Choose the center you want to attend when you register."
    }
]

export const registrationSteps: Array<Feature> = [
    {
        title: "We call you",
        text: "Our team calls your mobile number to talk through your track, batch timings and fees.",
        icon: Phone
    },
    {
        title: "You join a batch",
        text: "Choose a batch at your training center and complete your enrollment.",
        icon: Users
    },
    {
        title: "Your portal goes live",
        text: "Sign in with your registered mobile number to follow your training.",
        icon: LogIn
    }
]

export const qualificationOptions: Array<SelectOption> = [
    { value: 'btech', label: "B.Tech" },
    { value: 'degree', label: "Degree" },
    { value: 'diploma', label: "Diploma" }
]

export const passingYearOptions: Array<SelectOption> = [
    { value: '2029', label: "2029" },
    { value: '2028', label: "2028" },
    { value: '2027', label: "2027" },
    { value: '2026', label: "2026" },
    { value: '2025', label: "2025" },
    { value: '2024', label: "2024" },
    { value: '2023', label: "2023" },
    { value: '2022', label: "2022" },
    { value: '2021-or-earlier', label: "2021 or earlier" }
]

export const centerOptions: Array<SelectOption> = centers.map((center) => ({ value: center.id, label: center.city }))

export const programOptions: Array<SelectOption> = [
    ...programs.map((program) => ({ value: program.id, label: program.title })),
    { value: 'not-sure', label: "Not sure yet, help me choose" }
]
